import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical, ListPlus, X } from 'lucide-react';
import api from '@/services/apiClient';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import { MUSCLE_LABELS, TECHNIQUES, TECHNIQUE_LABELS, TECHNIQUE_DESCRIPTIONS } from '@/lib/enums';
import styles from './WorkoutBuilder.module.css';

const newRow = (exerciseId, name, imageUrl) => ({
  exerciseId, name, imageUrl: imageUrl || '', sets: 3, reps: '12', loadKg: '', restSeconds: 60,
  technique: '', supersetGroup: '', notes: '', custom: false, setDetails: [],
});

const detailToForm = (s) => ({
  type: s.type === 'warmup' ? 'warmup' : 'work',
  reps: s.reps ?? '',
  loadKg: s.loadKg != null ? Number(s.loadKg) : '',
  restSeconds: s.restSeconds ?? '',
});

// Expande o agregado (4 × 10 · 40kg) em linhas de série editáveis.
const expandAggregate = (r) =>
  Array.from({ length: Number(r.sets) || 1 }, () => ({
    type: 'work',
    reps: r.reps || '',
    loadKg: r.loadKg,
    restSeconds: r.restSeconds,
  }));

/**
 * Builder de treino. `template` → cria modelo (sem aluno, com categoria).
 * `workout` → edita um treino/modelo existente (pré-preenche e salva via PUT).
 * onSaved() é chamado após salvar.
 */
export default function WorkoutBuilder({ studentId, template = false, workout = null, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(() => ({
    name: workout?.name || '',
    label: workout?.label || '',
    focus: workout?.focus || '',
    templateCategory: workout?.templateCategory || '',
    notes: workout?.notes || '',
  }));
  const [rows, setRows] = useState(() =>
    (workout?.exercises || []).map((e) => ({
      exerciseId: e.exerciseId,
      name: e.exercise?.name || '',
      imageUrl: e.exercise?.imageUrl || '',
      sets: e.sets ?? 3,
      reps: e.reps ?? '',
      // loadKg chega como decimal string ("40.00") — normaliza pra número
      loadKg: e.loadKg != null ? Number(e.loadKg) : '',
      restSeconds: e.restSeconds ?? '',
      technique: e.technique || '',
      supersetGroup: e.supersetGroup || '',
      notes: e.notes || '',
      custom: !!e.hasCustomSets,
      setDetails: e.hasCustomSets ? (e.setDetails || []).map(detailToForm) : [],
    }))
  );
  const [library, setLibrary] = useState([]);
  const [q, setQ] = useState('');
  const [loadingLib, setLoadingLib] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get('/exercises', { scope: 'all' })
      .then((res) => setLibrary((res.data || res).exercises || res.data || []))
      .catch(() => setLibrary([]))
      .finally(() => setLoadingLib(false));
  }, []);

  const setMetaField = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addExercise = (ex) => {
    if (rows.some((r) => r.exerciseId === ex.id)) return;
    setRows((rs) => [...rs, newRow(ex.id, ex.name, ex.imageUrl)]);
  };
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const toggleCustom = (i) =>
    setRows((rs) =>
      rs.map((r, idx) => {
        if (idx !== i) return r;
        if (r.custom) return { ...r, custom: false };
        return { ...r, custom: true, setDetails: r.setDetails.length ? r.setDetails : expandAggregate(r) };
      })
    );
  const setSetField = (i, sIdx, k, v) =>
    setRows((rs) =>
      rs.map((r, idx) =>
        idx === i
          ? { ...r, setDetails: r.setDetails.map((s, j) => (j === sIdx ? { ...s, [k]: v } : s)) }
          : r
      )
    );
  const addSet = (i, type) =>
    setRows((rs) =>
      rs.map((r, idx) => {
        if (idx !== i) return r;
        const line = { type, reps: type === 'warmup' ? '' : r.reps || '', loadKg: '', restSeconds: type === 'warmup' ? 45 : r.restSeconds };
        // Aquecimentos ficam antes das séries de trabalho.
        const details = type === 'warmup'
          ? [...r.setDetails.filter((s) => s.type === 'warmup'), line, ...r.setDetails.filter((s) => s.type !== 'warmup')]
          : [...r.setDetails, line];
        return { ...r, setDetails: details };
      })
    );
  const removeSet = (i, sIdx) =>
    setRows((rs) =>
      rs.map((r, idx) => (idx === i ? { ...r, setDetails: r.setDetails.filter((_, j) => j !== sIdx) } : r))
    );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return library.filter((e) => !term || e.name.toLowerCase().includes(term)).slice(0, 40);
  }, [library, q]);

  const save = async () => {
    if (!meta.name.trim()) return setErr(t('workouts.errNameRequired'));
    if (rows.length === 0) return setErr(t('workouts.errExerciseRequired'));
    setSaving(true);
    setErr(null);
    const body = {
      name: meta.name,
      label: meta.label || undefined,
      focus: meta.focus || undefined,
      notes: meta.notes || undefined,
      isTemplate: template,
      templateCategory: template ? meta.templateCategory || undefined : undefined,
      studentId: template ? undefined : studentId,
      exercises: rows.map((r, i) => {
        const custom = r.custom && r.setDetails.length > 0;
        const details = custom
          ? [
              ...r.setDetails.filter((s) => s.type === 'warmup'),
              ...r.setDetails.filter((s) => s.type !== 'warmup'),
            ].map((s, idx) => ({
              setNumber: idx + 1,
              type: s.type,
              reps: s.reps || null,
              loadKg: s.loadKg === '' || s.loadKg == null ? null : Number(s.loadKg),
              restSeconds: s.restSeconds === '' || s.restSeconds == null ? null : Number(s.restSeconds),
            }))
          : undefined;
        return {
          exerciseId: r.exerciseId,
          order: i,
          sets: Number(r.sets) || undefined,
          reps: r.reps || undefined,
          loadKg: r.loadKg ? Number(r.loadKg) : undefined,
          restSeconds: r.restSeconds ? Number(r.restSeconds) : undefined,
          technique: r.technique || undefined,
          supersetGroup: r.supersetGroup ? Number(r.supersetGroup) : undefined,
          notes: r.notes || undefined,
          setDetails: details,
        };
      }),
    };
    try {
      if (workout) await api.put(`/workouts/${workout.id}`, body);
      else await api.post('/workouts', body);
      onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Input label={t('workouts.workoutName')} value={meta.name} onChange={setMetaField('name')} placeholder={t('workouts.workoutNamePlaceholder')} />
        <Input label={t('workouts.label')} value={meta.label} onChange={setMetaField('label')} placeholder="A" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Input label={t('workouts.focus')} value={meta.focus} onChange={setMetaField('focus')} placeholder={t('workouts.focusPlaceholder')} />
        {template && <Input label={t('workouts.templateCategory')} value={meta.templateCategory} onChange={setMetaField('templateCategory')} placeholder={t('workouts.templateCategoryPlaceholder')} />}
      </div>

      <div className={styles.builder}>
        <div className={styles.libCol}>
          <div className="kicker" style={{ marginBottom: 8 }}>{t('workouts.library')}</div>
          <Input placeholder={t('workouts.searchExercise')} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className={styles.libList}>
            {loadingLib ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={20} /></div>
            ) : filtered.map((ex) => (
              <button key={ex.id} type="button" className={styles.libItem} onClick={() => addExercise(ex)}>
                {ex.imageUrl ? (
                  <img className={styles.libThumb} src={ex.imageUrl} alt="" />
                ) : (
                  <span className={styles.libThumb} aria-hidden />
                )}
                <span>
                  <span className={styles.libName}>{ex.name}</span>
                  <Badge variant="accent">{MUSCLE_LABELS[ex.muscleGroup] || ex.muscleGroup}</Badge>
                </span>
                <Plus size={15} />
              </button>
            ))}
          </div>
        </div>

        <div className={styles.rowsCol}>
          <div className="kicker" style={{ marginBottom: 8 }}>{t('workouts.workoutExercises', { count: rows.length })}</div>
          <div className={styles.rows}>
            {rows.map((r, i) => (
              <div key={r.exerciseId} className={styles.exRow}>
                <div className={styles.exHead}>
                  <span className={styles.handle} aria-hidden><GripVertical size={16} strokeWidth={2} /></span>
                  {r.imageUrl ? (
                    <img className={styles.exThumb} src={r.imageUrl} alt="" />
                  ) : (
                    <span className={styles.exThumb} aria-hidden />
                  )}
                  <span className={styles.exName}>{i + 1}. {r.name}</span>
                  <button type="button" className={styles.setsToggle} onClick={() => toggleCustom(i)}>
                    <ListPlus size={13} />
                    {r.custom ? t('workouts.quickMode') : t('workouts.editSets')}
                  </button>
                  <button type="button" className={styles.del} onClick={() => removeRow(i)}><Trash2 size={14} /></button>
                </div>
                {!r.custom ? (
                  <div className={styles.exFields}>
                    <label>{t('workouts.sets')}<input type="number" value={r.sets} onChange={(e) => setRow(i, 'sets', e.target.value)} /></label>
                    <label>{t('workouts.reps')}<input value={r.reps} onChange={(e) => setRow(i, 'reps', e.target.value)} /></label>
                    <label>{t('workouts.load')}<input type="number" value={r.loadKg} onChange={(e) => setRow(i, 'loadKg', e.target.value)} /></label>
                    <label className={styles.rest}>{t('workouts.rest')}<input type="number" value={r.restSeconds} onChange={(e) => setRow(i, 'restSeconds', e.target.value)} /></label>
                  </div>
                ) : (
                  <div className={styles.setsBox}>
                    {['warmup', 'work'].map((sectionType) => {
                      const section = r.setDetails
                        .map((s, sIdx) => ({ s, sIdx }))
                        .filter(({ s }) => (sectionType === 'warmup' ? s.type === 'warmup' : s.type !== 'warmup'));
                      return (
                        <div key={sectionType}>
                          <div className={`${styles.setsKicker} ${sectionType === 'warmup' ? styles.warm : ''}`}>
                            {sectionType === 'warmup' ? t('workouts.warmup') : t('workouts.workSets')}
                          </div>
                          {section.map(({ s, sIdx }, n) => (
                            <div key={sIdx} className={styles.setLine}>
                              <span className={styles.setNum}>
                                {sectionType === 'warmup' ? 'AQ' : t('workouts.setShort')} {n + 1}
                              </span>
                              <label>{t('workouts.reps')}<input value={s.reps} onChange={(e) => setSetField(i, sIdx, 'reps', e.target.value)} /></label>
                              <label>{t('workouts.load')}<input type="number" value={s.loadKg} onChange={(e) => setSetField(i, sIdx, 'loadKg', e.target.value)} /></label>
                              <label>{t('workouts.rest')}<input type="number" value={s.restSeconds} onChange={(e) => setSetField(i, sIdx, 'restSeconds', e.target.value)} /></label>
                              <button type="button" className={styles.del} onClick={() => removeSet(i, sIdx)}><X size={13} /></button>
                            </div>
                          ))}
                          <button type="button" className={styles.addSetBtn} onClick={() => addSet(i, sectionType)}>
                            <Plus size={13} />
                            {sectionType === 'warmup' ? t('workouts.addWarmup') : t('workouts.addSet')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className={styles.exExtras}>
                  <label>{t('workouts.technique')}
                    <select value={r.technique} onChange={(e) => setRow(i, 'technique', e.target.value)}>
                      <option value="">—</option>
                      {TECHNIQUES.map((tech) => (
                        <option key={tech} value={tech}>{TECHNIQUE_LABELS[tech]}</option>
                      ))}
                    </select>
                  </label>
                  <label>{t('workouts.supersetGroup')}
                    <input type="number" value={r.supersetGroup} onChange={(e) => setRow(i, 'supersetGroup', e.target.value)} />
                  </label>
                </div>
                {r.technique && r.technique !== 'normal' && TECHNIQUE_DESCRIPTIONS[r.technique] && (
                  <p className={styles.techniqueHint}>{TECHNIQUE_DESCRIPTIONS[r.technique]}</p>
                )}
              </div>
            ))}
            <div className={styles.addZone}>
              <Plus size={16} strokeWidth={2} />
              {rows.length === 0 ? t('workouts.addZoneEmpty') : t('workouts.addZone')}
            </div>
          </div>

        </div>
      </div>

      <Textarea label={t('workouts.notes')} value={meta.notes} onChange={setMetaField('notes')} rows={2} />
      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="button" loading={saving} onClick={save}>{t('workouts.saveWorkout')}</Button>
      </div>
    </div>
  );
}
